import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { CustomPagination } from '@watter/shared-components';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DeleteNotificationPopup } from '../../components/deleteNotificationPopUp/DeleteNotificationPopup';
import NotificationAlert from '../../components/icons/notification-alert';
import { DELETE_NOTIFICATION, GET_ALL_NOTIFICATIONS, GET_UNREAD_NOTIFICATION_COUNT } from '../../graphql/notifications';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import {
    setAllNotificationsCleared,
    setAllNotificationsCount,
    setUnreadNotificationsCount,
} from '../../store/reducers/notificationsSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { formatDateWithTimeWithSeconds, getTimeAgo } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { NotifyContainer } from '../common-styles';
import { NotificationSettingPopUp } from './notification-setting-popup/notification-setting-popup';

const NotificationViewAll: React.FC = () => {
    const [notificationList, setNotificationsList] = useState([]);
    const [noNotificationMsg, setNoNotificationMsg] = useState('');
    const [totalCount, setTotalRecordCount] = React.useState(0);
    const rowsPerPage = 10;
    const [page, setCurrentPage] = React.useState(1);
    const [openDeleteModal, setDeleteModalOpen] = useState(false);
    const [deleteNotificationId, setNotificationIdToRemove] = useState(null);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const clearedAll = useSelector((state: any) => state.notifications.clearedAll);
    const navigate = useNavigate();
    const getUnreadNotificationCount = React.useCallback(async () => {
        try {
            const { data } = await client.query({
                query: GET_UNREAD_NOTIFICATION_COUNT,
                fetchPolicy: 'network-only',
            });
            if (data && data?.getUnreadNotificationsCount) {
                dispatch(setUnreadNotificationsCount(data?.getUnreadNotificationsCount?.count));
            }
        } catch (error) {
            console.error('Error fetching unread notification count:', error);
        }
    }, [dispatch]);
    const getAllNotifications = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const { data } = await client.query({
                query: GET_ALL_NOTIFICATIONS,
                variables: {
                    limit: rowsPerPage,
                    page,
                    readAll: true,
                },
                fetchPolicy: 'network-only',
            });
            if (data && data?.getNotifications) {
                const listData = data?.getNotifications?.data;
                const modifiedListData = listData.map((item: any) =>
                    item?.type === 8 || item?.type === 7
                        ? {
                              ...item,
                              message: `${item?.message} at ${formatDateWithTimeWithSeconds(item?.meta_data?.sensor_timestamp)}`,
                          }
                        : item,
                );
                setNotificationsList(modifiedListData);
                setTotalRecordCount(data?.getNotifications?.total_count);
                if (clearedAll) {
                    getUnreadNotificationCount();
                }
                dispatch(setAllNotificationsCleared(false));
                dispatch(setAllNotificationsCount(data?.getNotifications?.total_count));
                dispatch(setUnreadNotificationsCount(0));
                if (data?.getNotifications?.data?.length === 0) {
                    setNoNotificationMsg(data?.getNotifications?.message);
                }
            }
            dispatch(hideLoader());
        } catch (error) {
            console.error('Error fetching user notifications:', error);
            dispatch(hideLoader());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, getUnreadNotificationCount, page, rowsPerPage]);
    const handleDeleteNotification = (item: any) => {
        setDeleteModalOpen(true);
        setNotificationIdToRemove(item?.id);
    };
    const handleConfirmDeleteNotification = async () => {
        try {
            dispatch(showLoader());
            const response = await client.mutate({
                mutation: DELETE_NOTIFICATION,
                variables: {
                    deleteNotificationId,
                },
            });
            if (response.data?.deleteNotification?.success) {
                dispatch(hideLoader());
                dispatch(
                    openToast({
                        show: true,
                        message: response.data?.deleteNotification?.message,
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
                setCurrentPage(1);
                getAllNotifications();
            } else {
                dispatch(hideLoader());
                dispatch(
                    openToast({
                        show: true,
                        message: t('deleted_notification_failed'),
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
            }
        } catch (error) {
            dispatch(hideLoader());
            console.error('Error deleting notification:', error);
        } finally {
            setDeleteModalOpen(false);
        }
    };
    React.useEffect(() => {
        getAllNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);
    React.useEffect(() => {
        setCurrentPage(1);
        getAllNotifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearedAll]);

    const matches = useMediaQuery('(max-width:767px)');
    const heightValue = matches ? 'calc(100dvh - 230px)' : 'calc(100dvh - 214px)';
    const handleNotificationClick = (item: any) => {
        const type = item?.type;
        switch (type) {
            case 1:
            case 2:
                navigate(NavigationConfig.ProfileSetting);
                break;
            default:
        }
    };
    return (
        <NotifyContainer className="container" my={2}>
            <Stack rowGap={1.5} height={heightValue} overflow="auto">
                {notificationList?.length > 0 ? (
                    notificationList?.map((item: any) => (
                        <Box
                            key={item?.id || item?.createdAt}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                            className="cards"
                            onClick={() => handleNotificationClick(item)}
                            data-testid={`notification-card-${item?.id}`}
                        >
                            <Stack>
                                <Typography variant="h6" fontWeight={700}>
                                    {item?.message}
                                </Typography>
                                {/* <Typography variant="subtitle1" className="subText">{item.message}</Typography> */}
                            </Stack>
                            <Typography variant="subtitle1" display="flex" alignItems="center" gap={1}>
                                {getTimeAgo(item?.createdAt)} {item?.read_status === 0 && <Box className="unread" />}
                                <DeleteIcon
                                    data-testid="delete-icon"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleDeleteNotification(item);
                                    }}
                                />
                            </Typography>
                        </Box>
                    ))
                ) : (
                    <Typography
                        variant="h6"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight={700}
                    >
                        {noNotificationMsg}
                    </Typography>
                )}
            </Stack>
            {notificationList?.length > 0 && (
                <CustomPagination
                    sx={{ padding: '16px 0px', display: 'flex', justifyContent: 'flex-end' }}
                    totalCount={totalCount}
                    rowsPerPage={rowsPerPage}
                    currentPage={page}
                    onPageChange={setCurrentPage}
                    data-testid="pagination"
                />
            )}

            <DeleteNotificationPopup
                open={openDeleteModal}
                heading={t('alert')}
                subHeading={t('deleteNotificationMsg')}
                acceptHandler={handleConfirmDeleteNotification}
                rejectHandler={() => setDeleteModalOpen(false)}
                iconComponent={<NotificationAlert />}
                acceptButtonLabel="Yes"
                rejectButtonLabel="Cancel"
            />
            <NotificationSettingPopUp getAllNotifications={getAllNotifications} />
        </NotifyContainer>
    );
};
export default NotificationViewAll;
